import { Drums } from '@ezchops/drums';
import { Events, notEmpty } from '@ezchops/helpers';
import { chain, orderBy, sum } from 'lodash';
import sortBy from 'lodash/sortBy';
export class DrumMappingRecorder extends Events.EventEmitter {
    inputAdapter;
    pedalState = 0;
    mapping;
    hits;
    _activeNote = null;
    messages;
    constructor(inputAdapter) {
        super();
        this.inputAdapter = inputAdapter;
        this.inputAdapter.addEventListener('message', this.handleMessage);
        this.inputAdapter.addEventListener('noteon', this.handleNoteOn);
        this.inputAdapter.addEventListener('controlchange', this.handleControlChange);
        this.mapping = new Map();
        this.messages = new Map();
        this.hits = new Map();
    }
    close() {
        this.inputAdapter.removeEventListener('message', this.handleMessage);
        this.inputAdapter.removeEventListener('noteon', this.handleNoteOn);
        this.inputAdapter.removeEventListener('controlchange', this.handleControlChange);
    }
    get recorded() {
        return Array.from(this.mapping.keys());
    }
    get progress() {
        if (this.activeNote == null) {
            return undefined;
        }
        return this.hits.get(this.activeNote) ?? new Map();
    }
    get config() {
        let maxVelocity = 0;
        for (const nm of this.mapping.values()) {
            for (const pm of nm) {
                if (pm.velocity) {
                    maxVelocity = Math.max(maxVelocity, pm.velocity[1]);
                }
            }
        }
        const velocity = maxVelocity <= 64 ? [0, 64] : undefined;
        const pads = orderBy(Array.from(this.mapping.values()).flat(), ['note', 'pad']);
        return { velocity, pads, module: this.inputAdapter.drumModule };
    }
    get conflicts() {
        const pads = Array.from(this.mapping.values()).flat();
        return chain(pads)
            .groupBy('pad')
            .filter(p => {
            if (p.length == 1) {
                return false;
            }
            else if (p.some(pm => pm.pedal == null)) {
                return true;
            }
            else {
                const intervals = sortBy(p.map(pm => pm.pedal).filter(notEmpty), ([m,]) => m);
                return intervals.some(([m, M], i) => {
                    const [n, N] = intervals[i + 1] ?? [Infinity, Infinity];
                    return M >= n;
                });
            }
        })
            .value();
    }
    set activeNote(value) {
        this._activeNote = value;
        if (value != null) {
            this.mapping.set(value, []);
            this.hits.set(value, new Map());
        }
        this.emit('updated', this.state);
    }
    get activeNote() {
        return this._activeNote;
    }
    get state() {
        return {
            config: this.config,
            conflicts: this.conflicts,
            progress: this.progress,
            activeNote: this.activeNote,
            messages: this.messages
        };
    }
    handleNoteOn = (event) => {
        if (this.activeNote == null) {
            return;
        }
        const noteMappings = this.mapping.get(this.activeNote) ?? [];
        const matchingMappings = noteMappings.filter(({ pad }) => pad == event.note);
        if (matchingMappings.length > 1) {
            throw new Error('Multiple mappings recorded');
        }
        const recordingClosedHiHat = this.activeNote == Drums.CLOSED_HI_HAT;
        const recordingOpenHiHat = !recordingClosedHiHat && this.activeNote == Drums.OPEN_HI_HAT;
        const receivedClosedHiHat = event.note == Drums.CLOSED_HI_HAT;
        const receivedOpenHihatWithPedal = recordingClosedHiHat && !receivedClosedHiHat && this.pedalState != 0;
        if (matchingMappings.length == 0) {
            const genericNoteMapping = {
                pad: event.note,
                note: this.activeNote,
                velocity: [0, event.velocity]
            };
            const noteMapping = receivedOpenHihatWithPedal // module sends open hihat and pedal
                ? { ...genericNoteMapping, pedal: [this.pedalState, 127] }
                : recordingOpenHiHat && this.pedalState != 0
                    ? { ...genericNoteMapping, pedal: [0, this.pedalState - 1] }
                    : genericNoteMapping;
            this.mapping.set(this.activeNote, [...noteMappings, noteMapping]);
        }
        else {
            const noteMapping = matchingMappings[0];
            if (noteMapping.velocity == null || noteMapping.velocity[1] < event.velocity) {
                noteMapping.velocity = [0, event.velocity];
            }
            if (recordingClosedHiHat && this.pedalState != 0 && (noteMapping.pedal == null || noteMapping.pedal[0] > this.pedalState)) {
                noteMapping.pedal = [this.pedalState, 127];
            }
            if (this.activeNote == Drums.OPEN_HI_HAT && this.pedalState != 0 && (noteMapping.pedal == null || noteMapping.pedal[1] < this.pedalState)) {
                noteMapping.pedal = [0, this.pedalState];
            }
        }
        this.updateProgress(event);
        this.emit('updated', this.state);
    };
    updateProgress = (event) => {
        if (this.activeNote == null) {
            return;
        }
        const noteHits = this.hits.get(this.activeNote) ?? new Map();
        const count = (noteHits.get(event.note) ?? 0) + 1;
        noteHits.set(event.note, count);
        this.hits.set(this.activeNote, noteHits);
        if (noteHits.size * 10 < sum(Array.from(noteHits.values()))) {
            this.activeNote = null;
        }
    };
    handleControlChange = (event) => {
        if (event.control == 4) {
            this.pedalState = event.value;
        }
    };
    handleMessage = (message) => {
        if (this.activeNote != null) {
            const messages = this.messages.get(this.activeNote);
            if (messages != null) {
                messages.push(message);
            }
            else {
                this.messages.set(this.activeNote, [message]);
            }
        }
    };
}
//# sourceMappingURL=DrumMappingRecorder.js.map